import React, { Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import CssBaseline from '@material-ui/core/CssBaseline'
import { ThemeProvider } from '@material-ui/core/styles'
import { StoreProvider } from 'easy-peasy'
import { LocationProvider } from '@reach/router'

import App from './App'
import theme from './theme'
import store from './store'
import { SingletonHooksContainer } from 'react-singleton-hook'
import mixpanel from 'mixpanel-browser'
import { MixpanelProvider } from 'react-mixpanel'
import * as Sentry from '@sentry/browser'
import { Integrations } from '@sentry/tracing'

import { FullPageSpinner } from './components'
import { AccountContextProvider, AudioVideoContextProvider } from './hooks'
import { ReactKeycloakProvider } from '@react-keycloak/web'
import keycloak, {
  isSsoSession,
  markSsoSession,
  clearSsoSession,
} from './keycloak'
import { authService } from './services/authService'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './queryClient'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

const resolveEnvironment = () => {
  const {
    location: { host },
  } = window
  if (!host) {
    return 'unknown'
  }

  if (host.match(/rendelo|clinic/i)) {
    return 'production'
  } else if (host.match(/tesztelo|tester/i)) {
    return 'staging'
  } else if (host.match(/localhost|127.0.0.1/i)) {
    return 'local'
  }

  return 'unknown'
}

Sentry.init({
  dsn: 'https://c156295774174ba88a5abdb00e2fa60f@o1077435.ingest.sentry.io/6080355',
  //release: process.env.REACT_APP_SENTRY_RELEASE,
  integrations: [new Integrations.BrowserTracing()],
  environment: resolveEnvironment(),
  tracesSampleRate: 1.0,
})

mixpanel.init('a423e6957f112b47b6ee4f79b53b3078')

// Sync Keycloak tokens into the app's existing auth plumbing.
// - Empty tokens (fired on auth errors/logout) are ignored here; session
//   teardown is handleKeycloakEvent's job.
// - A live legacy HS256 session is never overwritten: the browser may hold a
//   Keycloak cookie for a DIFFERENT account, and adopting its token would
//   silently switch identities (the backend resolves Keycloak tokens by email
//   and auto-provisions, so nothing would error).
// - Refreshes write localStorage only. The axios interceptor reads the token
//   from localStorage per request; re-dispatching auth.token every ~5 minutes
//   would re-run App.tsx's bootstrap effect (profile refetch behind a
//   full-screen backdrop + a leaked resize listener per refresh).
// The provider fires onEvent BEFORE onTokens (see @react-keycloak/core
// updateState), so this records which event produced the tokens below.
let lastKeycloakEvent = ''

const handleKeycloakTokens = (tokens: { token?: string }) => {
  if (!tokens.token) return
  const existingToken = localStorage.getItem('token')
  if (existingToken && !isSsoSession()) return
  // ADOPTING into an empty localStorage is only legitimate right after init
  // or an interactive login. A refresh event landing on empty storage means
  // an in-flight refresh XHR outlived a logout (clearToken stops the timer
  // but cannot cancel a request already on the wire) — adopting it would
  // silently resurrect the session that was just torn down.
  if (
    !existingToken &&
    lastKeycloakEvent !== 'onReady' &&
    lastKeycloakEvent !== 'onAuthSuccess'
  ) {
    return
  }
  localStorage.setItem('token', tokens.token)
  markSsoSession()
  if (!store.getState().auth.token) {
    store.dispatch.auth.setToken(tokens.token)
  }
}

// Watchdog for the init gate: keycloak-js 26's silent check registers a
// message listener with NO timeout — if the silent iframe never answers
// (silent-check-sso.html not whitelisted on the client, CSP frame-src, SPA
// fallback swallowing the file), neither onReady nor onInitError ever fires
// and App.tsx would spin forever. Real events cancel this. 12s: must sit
// ABOVE keycloak-js' own 10s messageReceiveTimeout (3p-cookie probe), or a
// slow-but-successful init gets pre-empted and a restorable session flashes
// through a 401 logout instead.
const keycloakInitWatchdog = window.setTimeout(() => {
  store.dispatch.auth.setKeycloakInitDone(true)
}, 12000)

const handleKeycloakEvent = (event: string) => {
  lastKeycloakEvent = event
  if (event === 'onReady') {
    window.clearTimeout(keycloakInitWatchdog)
    // Silent check-sso finished. If it could not restore a session but an
    // SSO token is still stored, that token is dead weight (the refresh
    // token lives only in keycloak-js memory) — drop it so App.tsx boots
    // straight to /login instead of firing a doomed 401 round trip.
    if (!keycloak.authenticated && isSsoSession()) {
      localStorage.removeItem('token')
      clearSsoSession()
      // Also reset the store copy: if the watchdog opened the gate early,
      // App.tsx "Case 2" may already have copied the dead token into the
      // store, and "Case 3" would re-persist it right after this wipe.
      store.dispatch.auth.resetAuthState()
    }
    store.dispatch.auth.setKeycloakInitDone(true)
  } else if (event === 'onInitError') {
    // Keycloak unreachable — unblock App.tsx rather than hang on the gate;
    // a stale SSO token then fails over to the normal 401 handling.
    window.clearTimeout(keycloakInitWatchdog)
    store.dispatch.auth.setKeycloakInitDone(true)
  } else if (event === 'onAuthLogout' && isSsoSession()) {
    // keycloak-js fires this from clearToken(), i.e. only when the session
    // is genuinely gone (refresh token rejected with 400). Without this
    // teardown the dead JWT stays in localStorage and the app keeps
    // rendering the authenticated shell until some request 401s.
    // onAuthRefreshError is deliberately NOT handled: it also fires on
    // transient network errors/5xx where keycloak-js keeps its tokens —
    // logging out there would turn a moment offline into a lost session.
    // endSsoSession=false — automatic paths must never trigger a
    // realm-wide Keycloak logout.
    authService.logout(false)
  }
}

type Props = StoreProvider['props'] & { children: React.ReactNode }
const StoreProviderCast = StoreProvider as unknown as React.ComponentType<Props>

const container = document.getElementById('root')
const root = createRoot(container!)

root.render(
  <StoreProviderCast store={store}>
    <ThemeProvider theme={theme}>
      <ReactKeycloakProvider
        authClient={keycloak}
        initOptions={{
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
          // Session-aware fallback. false for anonymous visitors: browsers
          // that block third-party cookies (Safari, Firefox ETP, incognito)
          // would otherwise bounce EVERY cold load full-page through
          // Keycloak. true for returning SSO sessions: there the fallback's
          // prompt=none redirect is the only way to restore the session
          // when the silent iframe can't see the SSO cookie — without it a
          // reload in those browsers kills a valid session.
          silentCheckSsoFallback: isSsoSession(),
          pkceMethod: 'S256',
          // Accepted risk: no session-status iframe, so a Keycloak-side
          // logout (other tab/client/admin) is only noticed at the next 401.
          checkLoginIframe: false,
        }}
        onEvent={handleKeycloakEvent}
        onTokens={handleKeycloakTokens}
      >
        <QueryClientProvider client={queryClient}>
          <MixpanelProvider mixpanel={mixpanel}>
            <AccountContextProvider>
              <AudioVideoContextProvider>
                {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
                <CssBaseline />
                <SingletonHooksContainer />
                <Suspense fallback={<FullPageSpinner />}>
                  {/* Single top-level LocationProvider for the @reach/router fork.
                      Covers the <Router> routes AND out-of-Router consumers such
                      as the side menu's <Link>s; without it the fork throws
                      "can't access property 'location'". See src/shims/reach-router.js. */}
                  <LocationProvider>
                    <App />
                    <ReactQueryDevtools initialIsOpen={false} />
                  </LocationProvider>
                </Suspense>
              </AudioVideoContextProvider>
            </AccountContextProvider>
          </MixpanelProvider>
        </QueryClientProvider>
      </ReactKeycloakProvider>
    </ThemeProvider>
  </StoreProviderCast>
)
